import { useState, useRef, useEffect, useCallback } from "react";
import { BusinessLogo } from "../../../images";
import "./whatsappChat.css";

const API_URL =
  "https://udbd6cbymx.eu-west-1.awsapprunner.com/v1/api/assistant/chat";
const ASSISTANT_ID = "AST_54DC6363F58D7BF";

function formatTime(date) {
  return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function parseXmlResponse(xmlText) {
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(xmlText, "text/xml");

    const parseError = doc.querySelector("parsererror");
    if (parseError) {
      // Not valid XML — try extracting text between tags with regex
      const tagContent = xmlText
        .replace(/<[^>]+>/g, " ")
        .replace(/\s+/g, " ")
        .trim();
      return tagContent || xmlText;
    }

    // Recursively extract all text content
    function extractText(node) {
      if (node.nodeType === Node.TEXT_NODE) {
        return node.textContent.trim();
      }
      const parts = [];
      for (const child of node.childNodes) {
        const text = extractText(child);
        if (text) parts.push(text);
      }
      return parts.join("\n");
    }

    const result = extractText(doc.documentElement);
    return result || xmlText;
  } catch {
    // Fallback: strip tags
    return xmlText
      .replace(/<[^>]+>/g, " ")
      .replace(/\s+/g, " ")
      .trim();
  }
}

// Icons
function SendIcon() {
  return (
    <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
      <path d="M1.101 21.757L23.8 12.028 1.101 2.3l.011 7.912 13.623 1.816-13.623 1.817-.011 7.912z" />
    </svg>
  );
}

function BackArrowIcon() {
  return (
    <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
      <path d="M12 4l1.4 1.4L7.8 11H20v2H7.8l5.6 5.6L12 20l-8-8 8-8z" />
    </svg>
  );
}

function VideoCallIcon() {
  return (
    <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
      <path d="M15 8v8H5V8h10m1-2H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.55 0 1-.45 1-1v-3.5l4 4v-11l-4 4V7c0-.55-.45-1-1-1z" />
    </svg>
  );
}

function PhoneCallIcon() {
  return (
    <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
      <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z" />
    </svg>
  );
}

function MoreIcon() {
  return (
    <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
      <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" />
    </svg>
  );
}

function CheckIcon({ read }) {
  return (
    <svg viewBox="0 0 16 11" width="16" height="11" fill="none">
      <path
        d="M11.071.653a.457.457 0 0 0-.304-.102.493.493 0 0 0-.381.178l-6.19 7.636-2.405-2.272a.463.463 0 0 0-.336-.146.47.47 0 0 0-.343.146l-.311.31a.445.445 0 0 0-.14.337c0 .136.046.249.14.337l2.995 2.83a.63.63 0 0 0 .439.18c.19 0 .347-.073.47-.22L11.28 1.37a.46.46 0 0 0 .102-.39.505.505 0 0 0-.311-.327z"
        fill={read ? "#53bdeb" : "#667781"}
      />
      <path
        d="M14.071.653a.457.457 0 0 0-.304-.102.493.493 0 0 0-.381.178l-6.19 7.636-1.2-1.134.695-.858 .506.478a.63.63 0 0 0 .439.18c.19 0 .347-.073.47-.22L14.28 1.37a.46.46 0 0 0 .102-.39.505.505 0 0 0-.311-.327z"
        fill={read ? "#53bdeb" : "#667781"}
      />
    </svg>
  );
}

function TypingIndicator() {
  return (
    <div className="flex items-end gap-1 mb-1">
      <div className="max-w-[75%] bg-white rounded-lg rounded-tl-none px-3 py-2 shadow-md">
        <div className="flex items-center gap-1">
          <div className="flex gap-[3px]">
            <span className="w-[7px] h-[7px] bg-[#667781] rounded-full animate-bounce [animation-delay:0ms]" />
            <span className="w-[7px] h-[7px] bg-[#667781] rounded-full animate-bounce [animation-delay:150ms]" />
            <span className="w-[7px] h-[7px] bg-[#667781] rounded-full animate-bounce [animation-delay:300ms]" />
          </div>
        </div>
      </div>
    </div>
  );
}

function MessageBubble({ message }) {
  const isSent = message.type === "sent";

  return (
    <div
      className={`flex mb-3 animate-[fadeSlideIn_0.2s_ease-out] ${
        isSent ? "justify-end" : "justify-start"
      }`}
    >
      <div
        className={`max-w-[75%] px-[9px] pt-[6px] pb-[8px] rounded-lg shadow-md relative ${
          isSent
            ? "bg-[#d9fdd3] rounded-tr-none"
            : "bg-white rounded-tl-none"
        }`}
      >
        <p className="text-[14.2px] text-[#111b21] leading-[19px] whitespace-pre-wrap break-words">
          {message.text}
        </p>
        <div
          className={`flex items-center gap-1 mt-[2px] ${isSent ? "justify-end" : "justify-end"}`}
        >
          <span className="text-[11px] text-[#667781] leading-none">
            {formatTime(message.timestamp)}
          </span>
          {isSent && <CheckIcon read={true} />}
        </div>
      </div>
    </div>
  );
}

export default function WhatsAppChat() {
  const [messages, setMessages] = useState([
    {
      id: 1,
      type: "received",
      text: "Hello! How can I help you today? 👋",
      timestamp: new Date(),
    },
  ]);
  const [input, setInput] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const followupIdRef = useRef("");

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping, scrollToBottom]);

  const sendMessage = async () => {
    const text = input.trim();
    if (!text) return;

    const userMessage = {
      id: Date.now(),
      type: "sent",
      text,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInput("");
    setIsTyping(true);
    inputRef.current?.focus();

    try {
      const response = await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          query: text,
          followup_id: followupIdRef.current,
          language: "english",
          assistant_id: ASSISTANT_ID,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const res = await response.json();

      const data = res.data.response;

      // Save followup_id for conversation context
      if (res.data.followup_id) {
        followupIdRef.current = res.data.followup_id;
      }

      console.log("second", data);
      let parsedText;

      // Try JSON first, then XML
      try {
        const json = JSON.parse(data);
        // If JSON, extract the message field (adjust key as needed)
        parsedText =
          json.message ||
          json.response ||
          json.answer ||
          json.text ||
          json.data;
        if (typeof parsedText === "object") {
          parsedText = JSON.stringify(parsedText, null, 2);
        }
        // If the extracted value looks like XML, parse it further
        if (
          typeof parsedText === "string" &&
          parsedText.trim().startsWith("<")
        ) {
          parsedText = parseXmlResponse(parsedText);
        }
      } catch {
        // Not JSON — treat as XML or plain text
        if (data.trim().startsWith("<")) {
          parsedText = parseXmlResponse(data);
        } else {
          parsedText = data;
        }
      }

      const botMessage = {
        id: Date.now() + 1,
        type: "received",
        text: parsedText || "No response received.",
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, botMessage]);
    } catch (error) {
      const errorMessage = {
        id: Date.now() + 1,
        type: "received",
        text: `Sorry, something went wrong. Please try again.\n\n(${error.message})`,
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsTyping(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-[#d1d7db] p-4">
      {/* Phone frame */}
      <div className="w-full max-w-[420px] h-[90vh] max-h-[850px] flex flex-col bg-[#efeae2] rounded-2xl overflow-hidden shadow-2xl border border-[#d1d7db]/50">
        {/* Header */}
        <header className="flex items-center gap-2 bg-[#008069] px-2 py-[10px] shrink-0">
          <button className="text-white p-1 hover:bg-black/10 rounded-full transition-colors">
            <BackArrowIcon />
          </button>

          {/* Avatar */}
          <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center shrink-0 overflow-hidden">
            <img src={BusinessLogo} alt="avatar" />
          </div>

          {/* Contact info */}
          <div className="flex-1 min-w-0 ml-1">
            <h1 className="text-[16.5px] flex items-center gap-2 font-normal text-white leading-tight truncate text-left">
              Traderbot by NGX
              <span>
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <g clip-path="url(#clip0_24_1787)">
                    <path
                      d="M15.6667 8L14.04 6.14L14.2667 3.68L11.86 3.13333L10.6 1L8.33333 1.97333L6.06667 1L4.80667 3.12667L2.4 3.66667L2.62667 6.13333L1 8L2.62667 9.86L2.4 12.3267L4.80667 12.8733L6.06667 15L8.33333 14.02L10.6 14.9933L11.86 12.8667L14.2667 12.32L14.04 9.86L15.6667 8ZM7.06 11.1467L4.52667 8.60667L5.51333 7.62L7.06 9.17333L10.96 5.26L11.9467 6.24667L7.06 11.1467Z"
                      fill="#0096DE"
                    />
                  </g>
                  <defs>
                    <clipPath id="clip0_24_1787">
                      <rect width="16" height="16" fill="white" />
                    </clipPath>
                  </defs>
                </svg>
              </span>
            </h1>
            <p className="text-[13px] text-white/70 leading-tight text-left">
              online
            </p>
          </div>

          {/* Header actions */}
          <div className="flex items-center gap-1">
            <button className="text-white p-2 hover:bg-black/10 rounded-full transition-colors">
              <VideoCallIcon />
            </button>
            <button className="text-white p-2 hover:bg-black/10 rounded-full transition-colors">
              <PhoneCallIcon />
            </button>
            <button className="text-white p-2 hover:bg-black/10 rounded-full transition-colors">
              <MoreIcon />
            </button>
          </div>
        </header>

        {/* Chat area */}
        <div
          className="flex-1 overflow-y-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none] px-[10px] py-2 bg-[#efeae2] "
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='300' height='300' xmlns='http://www.w3.org/2000/svg'%3E%3Cdefs%3E%3Cpattern id='p' width='60' height='60' patternUnits='userSpaceOnUse'%3E%3Ccircle cx='10' cy='10' r='1' fill='%23d1d7db' opacity='0.5'/%3E%3Ccircle cx='40' cy='30' r='1' fill='%23d1d7db' opacity='0.3'/%3E%3Ccircle cx='25' cy='50' r='0.8' fill='%23d1d7db' opacity='0.4'/%3E%3C/pattern%3E%3C/defs%3E%3Crect width='300' height='300' fill='%23efeae2'/%3E%3Crect width='300' height='300' fill='url(%23p)'/%3E%3C/svg%3E")`,
          }}
        >
          {/* Date chip */}
          <div className="flex justify-center mb-3 mt-1">
            <span className="bg-white/80 text-[#54656f] text-[12.5px] px-3 py-[5px] rounded-lg shadow-sm">
              Today
            </span>
          </div>

          {messages.map((msg) => (
            <MessageBubble key={msg.id} message={msg} />
          ))}

          {isTyping && <TypingIndicator />}

          <div ref={messagesEndRef} />
        </div>

        {/* Input area */}
        <div className="flex items-end gap-[6px] bg-[#f0f2f5] px-2 py-[6px] shrink-0">
          {/* Emoji button placeholder */}
          <button className="text-[#667781] p-2 hover:bg-black/10 rounded-full transition-colors shrink-0">
            <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
              <path d="M9.153 11.603c.795 0 1.439-.879 1.439-1.962s-.644-1.962-1.439-1.962-1.439.879-1.439 1.962.644 1.962 1.439 1.962zm5.603 0c.795 0 1.439-.879 1.439-1.962s-.644-1.962-1.439-1.962-1.439.879-1.439 1.962.644 1.962 1.439 1.962zM12 2C6.477 2 2 6.477 2 12c0 5.523 4.477 10 10 10s10-4.477 10-10c0-5.523-4.477-10-10-10zm0 18c-4.418 0-8-3.582-8-8s3.582-8 8-8 8 3.582 8 8-3.582 8-8 8zm-1.108-4.822c-1.259-.172-2.258-.872-2.748-1.59a.394.394 0 0 0-.661.424c.623.972 1.875 1.834 3.409 2.044.148.02.3.032.452.032 1.535 0 3.078-.681 3.876-2.076a.394.394 0 0 0-.661-.424c-.724 1.059-2.122 1.59-3.334 1.59-.113 0-.226-.006-.333-.018z" />
            </svg>
          </button>

          {/* Text input */}
          <div className="flex-1 bg-white rounded-lg flex items-end min-h-[42px]">
            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Message"
              rows={1}
              className="flex-1 bg-transparent text-[#3b4a54] text-[15px] placeholder-[#667781] px-3 py-[10px] resize-none outline-none leading-[20px] max-h-[100px] overflow-y-auto"
              style={{ scrollbarWidth: "thin" }}
            />
          </div>

          {/* Send / Mic button */}
          {input.trim() ? (
            <button
              onClick={sendMessage}
              className="text-[#667781] p-2 hover:bg-black/10 rounded-full transition-colors shrink-0"
            >
              <SendIcon />
            </button>
          ) : (
            <button className="text-[#667781] p-2 hover:bg-black/10 rounded-full transition-colors shrink-0">
              <svg
                viewBox="0 0 24 24"
                width="24"
                height="24"
                fill="currentColor"
              >
                <path d="M11.999 14.942c2.001 0 3.531-1.53 3.531-3.531V4.35c0-2.001-1.53-3.531-3.531-3.531S8.469 2.35 8.469 4.35v7.061c0 2.001 1.53 3.531 3.53 3.531zm6.238-3.53c0 3.531-2.942 6.002-6.238 6.002s-6.238-2.471-6.238-6.002H4.761c0 3.884 3.06 7.061 6.764 7.649v3.119h.948V19.06c3.703-.588 6.764-3.766 6.764-7.649h-1z" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Global animations */}
      <style>{`
        @keyframes fadeSlideIn {
          from {
            opacity: 0;
            transform: translateY(8px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
}
